/* rt-softnav:v3 — keep the document alive while browsing the WHOLE storefront, so the booth and the
   bottom player keep playing.

   Audio cannot survive a full page load: the browser tears down the document and every child context
   with it, iframes included. The only way a deck keeps spinning from one page to the next is to not
   reload at all - fetch the next page, swap the content column, and leave the rest of the document
   standing.

   v1 (2026-08) intercepted only /products/ and /collections/. v2 (2026-10-07, Mikey: "player should
   stay active [while] I browse [the] entire site") swaps every same-origin storefront page in the SAME
   language. v3 (same night) fixes what an adversarial review + live test found in v2:
     - language switcher forms now return to the page on screen (return_to copied from the new page);
     - the mobile menu is fully reset after a submenu link (it reopened blank);
     - a link to the page you are on no longer reloads (logo on /, current pagination number);
     - Back/Forward follow the URL actually shown (facets sort entries, # links) and restore the scroll
       position; pushState happens before the new page's scripts run, so they read the right URL;
     - the outgoing page's intervals and window/document UI listeners are removed (homepage marquee and
       slider timers, the Dig keyboard handler piled up per visit), camera streams are stopped (barcode
       scanner), and an open lightbox is closed;
     - a record-page tracklist preview playing on a phone hands over to the bottom bar instead of dying;
     - the bottom bar is hidden only on the Dig page (as the layout does) and only while it is idle;
     - GET forms to any swappable URL swap (filters, search); off inside the theme editor.
   Real page loads are kept for cart, checkout, account, app proxies, password, discount links, files
   (HARD / FILE), a change of language, and anything inside [data-rt-softnav="off"]; the whole thing is
   still switched off by data-rt-softnav="off" on <html>.

   Only `.main-content-list` is replaced. The booth (section 'sidebar') and the bottom player bar are
   SIBLINGS of that column in layout/theme.liquid, so they are never touched. Events: "rt:before-swap"
   (old page still in place) and "rt:page-swapped" (new page in place). */
(function () {
  "use strict";

  if (!window.history || !window.fetch || !window.DOMParser || !window.URLSearchParams) return;
  if (document.documentElement.getAttribute("data-rt-softnav") === "off") return;
  if (window.Shopify && window.Shopify.designMode) return;

  var SWAP = ".main-content-list";
  // checked against the path with any /fr prefix removed
  var HARD = /^\/(cart|checkout|checkouts|account|apps|a|tools|password|admin|services|discount|gift_cards|challenge|cdn|wpm|recommendations|localization|sitemap)(\/|$|\.)/;
  var FILE = /\.[a-z0-9]{2,5}$/i;
  var busy = false;
  var shown = location.pathname + location.search;   // the URL whose content is on screen
  try { history.scrollRestoration = "manual"; } catch (e) {}

  function localePrefix(path) {
    var m = path.match(/^\/fr(?=\/|$)/);
    return m ? m[0] : "";
  }

  function localPath(u) {
    return u.pathname.replace(/^\/fr(?=\/|$)/, "") || "/";
  }

  function eligibleUrl(u) {
    if (u.origin !== location.origin) return false;
    if (localePrefix(u.pathname) !== localePrefix(location.pathname)) return false;
    var p = localPath(u);
    if (HARD.test(p) || FILE.test(p)) return false;
    return true;
  }

  /* ---- page-scoped timers and listeners -------------------------------------------------------
     Scripts that arrive with a page are marked data-rt-page. Intervals they start and UI listeners they
     put on window/document (also from their DOMContentLoaded/load callbacks, which run late here) are
     recorded and removed when the page is swapped out. Timeouts end on their own; element listeners go
     with their elements. The first, hard-loaded page ran before this script and is not tracked. */
  var pageTimers = [];
  var pageListeners = [];
  var inPage = 0;
  var shimLoads = false;
  var TRACK = { keydown: 1, keyup: 1, keypress: 1, scroll: 1, resize: 1, wheel: 1, mousemove: 1,
                touchmove: 1, pointermove: 1, orientationchange: 1 };

  function isPageCode() {
    if (inPage > 0) return true;
    var cs = document.currentScript;
    return !!(cs && cs.hasAttribute && cs.hasAttribute("data-rt-page"));
  }

  var nativeSetInterval = window.setInterval;
  window.setInterval = function () {
    var id = nativeSetInterval.apply(window, arguments);
    if (isPageCode()) pageTimers.push(id);
    return id;
  };

  function runLate(target, type, cb) {
    setTimeout(function () {
      inPage++;
      try {
        if (typeof cb === "function") cb.call(target, new Event(type));
        else if (cb && cb.handleEvent) cb.handleEvent(new Event(type));
      } catch (e) { setTimeout(function () { throw e; }); }
      finally { inPage--; }
    }, 0);
  }

  function wrapAdd(target, nativeAdd) {
    return function (type, cb, opts) {
      // DOMContentLoaded / load fired long ago: call listeners from swapped-in scripts right away
      if (shimLoads && cb && (type === "DOMContentLoaded" || type === "load") && isPageCode()) {
        runLate(target, type, cb);
        return;
      }
      if (cb && TRACK[type] && isPageCode()) pageListeners.push([target, type, cb, opts]);
      return nativeAdd.call(this || target, type, cb, opts);
    };
  }
  document.addEventListener = wrapAdd(document, document.addEventListener);
  window.addEventListener = wrapAdd(window, window.addEventListener);

  function teardownPage(here) {
    pageTimers.forEach(function (id) { clearInterval(id); });
    pageTimers = [];
    pageListeners.forEach(function (l) {
      try { l[0].removeEventListener(l[1], l[2], l[3]); } catch (e) {}
    });
    pageListeners = [];
    // camera / microphone streams the outgoing page opened (barcode scanner)
    here.querySelectorAll("video, audio").forEach(function (m) {
      var so = m.srcObject;
      if (so && so.getTracks) so.getTracks().forEach(function (t) { try { t.stop(); } catch (e) {} });
    });
  }

  /* Component files that declare top-level classes can run only once per document: a second run throws
     "Identifier 'FacetFiltersForm' has already been declared" and does nothing else. Run them the first time
     a page brings them, then leave them be. */
  var ONCE = /\/facets\.js$/;
  var ranOnce = {};
  function onceKey(src) {
    try { var p = new URL(src, location.href).pathname; return ONCE.test(p) ? p : null; } catch (e) { return null; }
  }
  document.querySelectorAll("script[src]").forEach(function (s) {
    var k = onceKey(s.src);
    if (k) ranOnce[k] = true;
  });

  /* Scripts inserted via innerHTML never execute. Re-create them so section JS on the incoming page
     runs exactly as it would on a real load. */
  function runScripts(root) {
    var pending = 0;
    shimLoads = true;
    function done() { if (--pending <= 0) shimLoads = false; }
    var scripts = root.querySelectorAll("script");
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var type = (old.getAttribute("type") || "").toLowerCase();
      var once = old.src ? onceKey(old.src) : null;
      if (once) {
        if (ranOnce[once]) continue;
        ranOnce[once] = true;
      }
      var s = document.createElement("script");
      for (var j = 0; j < old.attributes.length; j++) {
        s.setAttribute(old.attributes[j].name, old.attributes[j].value);
      }
      s.setAttribute("data-rt-page", "");
      s.textContent = old.textContent;
      // a failed load must not block the next page from trying again
      if (once) s.addEventListener("error", (function (k) { return function () { delete ranOnce[k]; }; })(once));
      if (old.src && (type === "" || type.indexOf("javascript") !== -1)) {
        pending++;
        s.addEventListener("load", done);
        s.addEventListener("error", done);
      }
      old.parentNode.replaceChild(s, old);
    }
    if (pending === 0) shimLoads = false;
    else setTimeout(function () { shimLoads = false; }, 8000); // never leave the shim on
  }

  /* Shopify binds its pageview on load, so a swapped page would never be counted. Report it by hand
     rather than quietly under-reporting traffic. */
  function countPageview() {
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib &&
          window.ShopifyAnalytics.lib.page) {
        window.ShopifyAnalytics.lib.page(null, {
          path: location.pathname + location.search,
          title: document.title
        });
      }
    } catch (e) { /* analytics must never break navigation */ }
  }

  /* Overlays that belong to the header / sidebar survive the swap, so close them: mobile menu (with
     Dawn's own cleanup, which needs an element to hand focus to), header search, localization pickers,
     cart drawer, an open product lightbox. The booth section is never touched. */
  function closeOverlays() {
    var ev = new Event("rt-softnav");
    try {
      document.querySelectorAll("header-drawer, menu-drawer").forEach(function (d) {
        if (d.querySelector("details[open]") && typeof d.closeMenuDrawer === "function") {
          d.closeMenuDrawer(ev, d.querySelector("summary"));
        }
        d.querySelectorAll(".submenu-open").forEach(function (x) { x.classList.remove("submenu-open"); });
        d.querySelectorAll(".menu-opening").forEach(function (x) { x.classList.remove("menu-opening"); });
        // Dawn resets aria-expanded only for a KeyboardEvent. On the menu button that attribute alone
        // draws the drawer's dark ::before overlay (base.css), which then takes the next tap and
        // reopens the menu instead of following the link underneath.
        d.querySelectorAll('summary[aria-expanded="true"]').forEach(function (s) {
          s.setAttribute("aria-expanded", "false");
        });
      });
      var sh = document.querySelector(".section-header.menu-open");
      if (sh) sh.classList.remove("menu-open");
      // scroll locks a drawer adds on open: Dawn's overflow-hidden-<breakpoint>, and the layout's
      // remove-scroll, which otherwise waits for the details toggle at the end of the close animation
      document.body.classList.remove("overflow-hidden-mobile", "overflow-hidden-tablet",
        "overflow-hidden-desktop", "remove-scroll");
      document.querySelectorAll("details-modal").forEach(function (m) {
        if (m.querySelector("details[open]") && typeof m.close === "function") m.close(false);
      });
      var cart = document.querySelector("cart-drawer.active");
      if (cart && typeof cart.close === "function") cart.close();
      document.querySelectorAll("body > product-modal[open], body > modal-dialog[open]").forEach(function (m) {
        if (typeof m.hide === "function") m.hide();
      });
      document.querySelectorAll(".sidebar details[open]").forEach(function (d) {
        if (!d.closest("cart-drawer")) d.removeAttribute("open");
      });
    } catch (e) { /* a stuck overlay is better than a broken navigation */ }
  }

  /* A record-page tracklist preview plays in an <audio> inside the column on phones (the booth takes it
     on desktop). Hand a playing one to the persistent bottom bar so the music continues. */
  function handoffAudio(here) {
    var a = here.querySelector("#rt-pa-audio");
    if (!a || a.paused || !(a.currentSrc || a.src)) return;
    // rt-lazy-player:v1 - the bar's player.js may not be loaded yet (it loads on the first
    // play); read everything now, while the old page is still here, then load it and play.
    if (typeof window.changeSong !== "function" && typeof window.rtLoadPlayer !== "function") return;
    var box = here.querySelector("#rt-pp-audio");
    var row = here.querySelector("#rt-pp-audio .rt-pa-row.on");
    var title = row ? (row.getAttribute("data-name") ||
      ((row.querySelector(".rt-pa-name") || {}).textContent) || "") : "";
    var artist = box ? (box.getAttribute("data-artist") || "") : "";
    var image = box ? (box.getAttribute("data-image") || "") : "";
    var url = a.currentSrc || a.src;
    var t = a.currentTime;
    try { a.pause(); } catch (e) {}
    var ready = typeof window.changeSong === "function" ? Promise.resolve() : window.rtLoadPlayer();
    ready.then(function () {
      if (typeof window.changeSong !== "function") return;
      return window.changeSong(url, image, String(title).trim(), artist);
    }).then(function () {
      if (t > 1 && typeof window.rtPlayerSeek === "function") window.rtPlayerSeek(t);
    }).catch(function () {});
  }

  /* The layout leaves the bottom bar out on the Dig page, which has its own player. After a swap: hide
     it there while it is idle, show it everywhere else (Toronto and Detroit keep it, as on a real load). */
  function syncBottomBar(doc, url) {
    var bar = document.querySelector(".mobile-player-container");
    if (!bar) return;
    var cls = (doc && doc.body && doc.body.getAttribute("class")) || "";
    var dig = /(^|\s)template-page-digging(\s|$)/.test(cls) ||
              localPath(new URL(url, location.href)) === "/pages/digging";
    var playing = typeof window.rtPlayerIsPlaying === "function" && window.rtPlayerIsPlaying();
    bar.style.display = dig && !playing ? "none" : "";
  }

  /* The header's language forms carry return_to = the page Shopify rendered. Keep them on this page. */
  function syncReturnTo(doc, url) {
    var src = doc && doc.querySelector('form[action$="/localization"] input[name="return_to"]');
    var u = new URL(url, location.href);
    var val = src ? src.getAttribute("value") : u.pathname + u.search;
    document.querySelectorAll('form[action$="/localization"] input[name="return_to"]').forEach(function (i) {
      i.value = val;
    });
  }

  /* The <head>, the header and the footer stay up across a swap, so bring over what differs per page:
     canonical, description, robots, Open Graph / Twitter tags, hreflang links and JSON-LD (search engines
     and link previews that run JS read them); the menu's active-item markers; and the footer, which carries
     more columns on some templates. */
  var HEAD_SEL = 'link[rel="canonical"], link[rel="alternate"][hreflang], meta[name="description"], ' +
    'meta[name="robots"], meta[property^="og:"], meta[name^="twitter:"], script[type="application/ld+json"]';
  function syncHead(doc) {
    if (!doc || !doc.head) return;
    try {
      var fresh = doc.head.querySelectorAll(HEAD_SEL);
      document.head.querySelectorAll(HEAD_SEL).forEach(function (n) { n.parentNode.removeChild(n); });
      fresh.forEach(function (n) { document.head.appendChild(document.importNode(n, true)); });
    } catch (e) {}
  }

  var ACTIVE = ["list-menu__item--active", "menu-drawer__menu-item--active", "header__active-menu-item"];
  function copyActive(el, src) {
    for (var j = 0; j < ACTIVE.length; j++) el.classList.toggle(ACTIVE[j], !!src && src.classList.contains(ACTIVE[j]));
    var cur = src && src.getAttribute("aria-current");
    if (cur) el.setAttribute("aria-current", cur); else el.removeAttribute("aria-current");
  }
  // Menu items carry ids (HeaderMenu-music-records, HeaderDrawer-music...), so pair them by id: the rest of
  // the header differs per page (the homepage logo is an <h1> with extra text).
  function syncMenuState(doc) {
    try {
      var here = document.querySelector(".section-header");
      var there = doc && doc.querySelector(".section-header");
      if (!here || !there) return;
      here.querySelectorAll('[id^="HeaderMenu-"], [id^="HeaderDrawer-"]').forEach(function (el) {
        var src = doc.getElementById(el.id);
        copyActive(el, src);
        var a = el.querySelectorAll("span"), b = src ? src.querySelectorAll("span") : [];
        if (a.length === b.length) for (var i = 0; i < a.length; i++) copyActive(a[i], b[i]);
      });
    } catch (e) {}
    // JSON-LD printed outside <head> and the swapped column on some pages only (the homepage's WebSite block
    // sits in the persistent sidebar area): drop blocks the new page lacks, add the ones it has. Only these
    // inert <script type="application/ld+json"> nodes are touched, never the booth itself.
    try {
      var J = 'script[type="application/ld+json"]';
      var loose = function (root) {
        return [].filter.call(root.querySelectorAll(J), function (s) { return !s.closest("head") && !s.closest(SWAP); });
      };
      var want = loose(doc).map(function (s) { return s.textContent.trim(); });
      loose(document).forEach(function (s) {
        var i = want.indexOf(s.textContent.trim());
        if (i === -1) s.parentNode.removeChild(s); else want.splice(i, 1);
      });
      want.forEach(function (t) {
        var s = document.createElement("script");
        s.type = "application/ld+json";
        s.textContent = t;
        document.body.appendChild(s);
      });
    } catch (e) {}
  }

  function syncFooter(doc) {
    try {
      document.querySelectorAll(".shopify-section-group-footer-group").forEach(function (s) {
        var n = doc && s.id && doc.getElementById(s.id);
        if (n && n.innerHTML !== s.innerHTML) s.innerHTML = n.innerHTML;
      });
    } catch (e) {}
  }

  function rememberScroll() {
    try {
      var st = {};
      if (history.state && typeof history.state === "object") {
        for (var k in history.state) st[k] = history.state[k];
      }
      st.rtSoftnav = true;
      st.y = window.scrollY;
      history.replaceState(st, "", location.href);
    } catch (e) {}
  }

  /* Other scripts change the URL in place: facets.js pushes an entry per filter/sort change, the variant
     picker replaces it. Keep `shown` on that URL. Otherwise Back from a sort to the page's own entry has the
     same URL as the stale `shown`, passes for a #fragment change, and nothing re-renders: the grid stayed
     sorted under the unsorted URL (facets.js leaves rtSoftnav entries to us). With `shown` current, that
     Back is a swap; the entry being left keeps its scroll position for it. A facets entry only gains y and
     stays facets.js's (re-rendered in place); any other entry, e.g. a #fragment one with no state, is
     marked rtSoftnav so facets.js does not also render it with searchParams undefined. */
  var nativePush = history.pushState;
  var nativeReplace = history.replaceState;
  history.pushState = function () {
    if (!busy) {
      try {
        var st = {};
        if (history.state && typeof history.state === "object") {
          for (var k in history.state) st[k] = history.state[k];
        }
        if (st.searchParams === undefined) st.rtSoftnav = true;
        st.y = window.scrollY;
        nativeReplace.call(history, st, "", location.href);
      } catch (e) {}
    }
    var r = nativePush.apply(this, arguments);
    shown = location.pathname + location.search;
    return r;
  };
  history.replaceState = function () {
    var r = nativeReplace.apply(this, arguments);
    shown = location.pathname + location.search;
    return r;
  };

  function hardNav(url) { window.location.assign(url); }

  /* A second Back/Forward while a Back/Forward swap is still loading is lost: popstate finds `busy`, or a
     `shown` that is not updated yet. The page that lands then sits under an entry the browser has already
     left (Back Back from a product: Records under "/"). Catch up with the entry the browser is on now.
     Compare with the URL the swap started for, not `shown`: a redirect moves `shown` but not the address
     bar, and must not swap again. */
  function catchUp(startedFor) {
    if (location.pathname + location.search === startedFor) return;
    if (!eligibleUrl(new URL(location.href))) { location.reload(); return; }
    var st = history.state || {};
    swap(location.href, false, typeof st.y === "number" ? st.y : 0);
  }

  function swap(url, push, restoreY) {
    if (busy) return;
    // a document that was loaded on the Dig page has no bottom bar to bring back: reload instead
    if (!document.querySelector(".mobile-player-container") && !/\/pages\/digging(\/|$|\?|#)/.test(url)) {
      hardNav(url);
      return;
    }
    busy = true;
    document.documentElement.classList.add("rt-softnav-loading");
    var finalUrl = url;
    var from = new URL(url, location.href);
    var startedFor = from.pathname + from.search;
    var swapped = false;

    fetch(url, { credentials: "same-origin", headers: { "X-Requested-With": "rt-softnav" } })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        var type = r.headers.get("content-type") || "";
        if (type.indexOf("text/html") === -1) throw new Error("not html");
        // a redirect may land somewhere that needs a real load (cart, another language, another site)
        if (r.redirected && r.url) {
          var landed = new URL(r.url);
          if (!eligibleUrl(landed)) { finalUrl = r.url; throw new Error("redirected out"); }
          finalUrl = landed.href.split("#")[0] + (new URL(url, location.href).hash || "");
        }
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var next = doc.querySelector(SWAP);
        var here = document.querySelector(SWAP);
        // If the incoming page is not shaped like the current one, do not guess - hand it to the browser.
        if (!next || !here) throw new Error("no swap target");

        document.dispatchEvent(new CustomEvent("rt:before-swap", { detail: { url: finalUrl } }));
        if (push) rememberScroll();
        closeOverlays();
        handoffAudio(here);
        teardownPage(here);
        here.parentNode.replaceChild(next, here);

        // the URL first, so the new page's scripts read their own location
        if (push) history.pushState({ rtSoftnav: true }, "", finalUrl);
        var fu = new URL(finalUrl, location.href);
        shown = fu.pathname + fu.search;
        document.title = doc.title || document.title;
        var bodyClass = doc.body && doc.body.getAttribute("class");
        if (bodyClass) document.body.setAttribute("class", bodyClass);

        runScripts(next);
        syncBottomBar(doc, finalUrl);
        syncReturnTo(doc, finalUrl);
        syncHead(doc);
        syncMenuState(doc);
        syncFooter(doc);
        try {
          /* global FacetFiltersForm */
          if (typeof FacetFiltersForm !== "undefined") FacetFiltersForm.searchParamsPrev = location.search.slice(1);
        } catch (e) {}

        var target = null;
        if (fu.hash.length > 1) {
          try { target = document.getElementById(decodeURIComponent(fu.hash.slice(1))); } catch (e) {}
        }
        if (typeof restoreY === "number") {
          window.scrollTo(0, restoreY);
          setTimeout(function () { window.scrollTo(0, restoreY); }, 350); // after late images
        } else if (target) {
          target.scrollIntoView();
        } else {
          window.scrollTo(0, 0);
        }
        var main = document.getElementById("MainContent");
        if (main && main.focus) main.focus({ preventScroll: true });

        countPageview();
        // anything that decorates the page can re-run off this
        document.dispatchEvent(new CustomEvent("rt:page-swapped", {
          detail: { url: finalUrl }
        }));
        swapped = true;
      })
      .catch(function () { hardNav(finalUrl); })
      .then(function () {
        busy = false;
        document.documentElement.classList.remove("rt-softnav-loading");
        if (swapped && !push) catchUp(startedFor);
      });
  }

  document.addEventListener("click", function (event) {
    var a = event.target.closest && event.target.closest("a[href]");
    if (!a || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (a.target && a.target !== "_self") return;
    if (a.hasAttribute("download")) return;
    if (a.closest('[data-rt-softnav="off"]')) return;
    var href = a.getAttribute("href");
    if (!href || href.charAt(0) === "#") return;
    if (/^(mailto|tel|sms|javascript):/i.test(href)) return;
    var u;
    try { u = new URL(href, location.href); } catch (e) { return; }
    if (!eligibleUrl(u)) return;
    if (u.pathname + u.search === location.pathname + location.search) {
      // same page: a #fragment is the page scrolling itself; no fragment would be a full reload
      if (u.hash) return;
      event.preventDefault();
      closeOverlays();
      window.scrollTo(0, 0);
      return;
    }
    event.preventDefault();
    swap(u.href, true);
  });

  /* GET forms to a swappable URL (header search box, /search page, collection filters): same swap. */
  document.addEventListener("submit", function (event) {
    var f = event.target;
    if (!f || f.tagName !== "FORM" || event.defaultPrevented) return;
    if ((f.getAttribute("method") || "get").toLowerCase() !== "get") return;
    if (f.target && f.target !== "_self") return;
    if (f.closest('[data-rt-softnav="off"]')) return;
    var u;
    try { u = new URL(f.getAttribute("action") || location.href, location.href); } catch (e) { return; }
    if (!eligibleUrl(u)) return;
    var params = new URLSearchParams();
    new FormData(f).forEach(function (v, k) { if (typeof v === "string") params.append(k, v); });
    u.search = params.toString();
    u.hash = "";
    event.preventDefault();
    swap(u.href, true);
  });

  window.addEventListener("popstate", function (e) {
    var now = location.pathname + location.search;
    if (now === shown) return;                       // a #fragment change: the browser scrolls
    var st = e.state || {};
    // a facets sort/filter entry on the same collection: facets.js re-renders the grid itself
    if (st.searchParams !== undefined && !st.rtSoftnav && location.pathname === shown.split("?")[0]) {
      shown = now;
      return;
    }
    if (!eligibleUrl(new URL(location.href))) { location.reload(); return; }
    swap(location.href, false, typeof st.y === "number" ? st.y : 0);
  });

  // so the first Back out of a swapped page returns here rather than reloading
  try { history.replaceState({ rtSoftnav: true, y: window.scrollY }, "", location.href); } catch (e) {}
})();
